import { Drawer, Form, Input } from 'antd';
import React, { FC, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import styles from './SearchDrawer.module.scss';
import { GlobalIcon, IconImage, GlobalSpin } from 'src/modules/global';
import { ActionCreatorAsyncType, BaseStore } from 'src/store';
import { connect } from 'react-redux';
import {
  fetchSearchSuggestionsData,
  fetchTopSearchedProductCategories,
  fetchTopSearchedProducts,
  SearchStore,
} from 'src/modules/search';
import { SearchSuggestions } from 'src/modules/search/components/searchSuggestions/SearchSuggestions';
import { MINIMUM_TERM_CHARACTERS_FOR_SUGGESTIONS } from 'src/modules/search/components/constants';
import { SearchRecommendations } from 'src/modules/search/components/searchRecommendations/SearchRecommendations';
import { EmptySuggestions } from 'src/modules/search/components/emptySuggestions/EmptySuggestions';
import { useRouter } from 'next/router';
import { useLocale } from 'src/modules/locale';

export interface SearchDrawerProps {
  onClose: () => void;
  isOpen: boolean;
}

export interface SearchDrawerProps extends SearchStore {
  fetchSearchSuggestionsData?: ActionCreatorAsyncType;
  fetchTopSearchedProducts?: ActionCreatorAsyncType;
  fetchTopSearchedProductCategories?: ActionCreatorAsyncType;
  isOpen: boolean;
  onClose: () => void;
}

const SearchDrawer: FC<SearchDrawerProps> = ({
  isOpen,
  onClose,
  fetchSearchSuggestionsData,
  fetchTopSearchedProducts,
  fetchTopSearchedProductCategories,
  isLoadingSuggestions,
  suggestions,
  topSearchedProducts,
  topSearchedProductCategories,
}) => {
  const [form] = Form.useForm();
  const t = useIntl();
  const [deleteIsShown, setDeleteIsShown] = useState(false);
  const router = useRouter();
  const { outputChannel: outputChannel } = useLocale();

  const getValue = useCallback(() => {
    return form.getFieldValue('term') || '';
  }, [form]);

  const canSearch = useCallback(() => {
    return getValue().length >= MINIMUM_TERM_CHARACTERS_FOR_SUGGESTIONS;
  }, [getValue]);

  const updateSuggestions = useCallback(() => {
    if (canSearch()) {
      fetchSearchSuggestionsData(getValue(), outputChannel);
    }
  }, [canSearch, fetchSearchSuggestionsData, getValue, outputChannel]);

  const onChange = useCallback(
    (newValue) => {
      if (!!newValue) {
        setDeleteIsShown(true);
      } else {
        setDeleteIsShown(false);
      }

      updateSuggestions();
    },
    [updateSuggestions]
  );

  const openSearchPage = useCallback(() => {
    onClose();
    // setFilters({
    //   productCategories: [],
    //   locationTypes: [],
    //   designers: [],
    // });
    if (getValue().length) {
      router.push({ pathname: `/search/${encodeURIComponent(getValue())}` });
    }
  }, [getValue, onClose, router]);

  const deleteText = useCallback(() => {
    form.setFieldsValue({ term: '' });
    setDeleteIsShown(false);
  }, [form]);

  // handle esc
  const closeOnEscapePress = (e) => {
    if (e.keyCode === 27) {
      onClose();
    }
  };

  // focus input on open
  const inputRef = useCallback(
    (node) => {
      if (node && isOpen) {
        // TODO: This seems to be a chrome bug when the input is not focused
        //       not ideal, but a small timeout seems to fix this
        // read more here https://www.xspdf.com/resolution/52164553.html
        setTimeout(() => {
          node.input.focus();
        }, 10);
      }
    },
    [isOpen]
  );

  useEffect(() => {
    if (isOpen) {
      fetchTopSearchedProducts(outputChannel);
      fetchTopSearchedProductCategories();
    }
  }, [fetchTopSearchedProductCategories, fetchTopSearchedProducts, isOpen, outputChannel]);

  return (
    <Drawer
      width="100%"
      height="100%"
      placement="bottom"
      visible={isOpen}
      closable={false}
      headerStyle={{ height: 0, overflow: 'hidden' }}
      className={styles.drawer}>
      <div className={styles.mainContainer}>
        <Form form={form} initialValues={{ term: '' }}>
          <div className={styles.header}>
            <Form.Item name={'term'} className={styles.inputContainer}>
              <Input
                ref={inputRef}
                tabIndex={-1}
                className={styles.input}
                type="text"
                {...{ autoComplete: 'off' }}
                onPressEnter={openSearchPage}
                onKeyUp={closeOnEscapePress}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                onSubmit={openSearchPage}
                placeholder={t.formatMessage({
                  id: 'search/placeholder',
                })}
              />
            </Form.Item>
            {deleteIsShown && (
              <div className={classnames(styles.button, styles.deleteButton)} onClick={deleteText}>
                <GlobalIcon
                  image={IconImage.Backspace}
                  size="custom"
                  className={styles.buttonIcon}
                />
              </div>
            )}
            <div className={styles.button} onClick={openSearchPage}>
              <GlobalIcon image={IconImage.Search} size="custom" className={styles.buttonIcon} />
            </div>
            <div className={styles.button} onClick={onClose}>
              <GlobalIcon image={IconImage.Close} size="custom" className={styles.buttonIcon} />
            </div>
          </div>
        </Form>
        <div className={styles.contents}>
          {isLoadingSuggestions && <GlobalSpin />}
          {canSearch() ? (
            suggestions.length ? (
              <SearchSuggestions suggestions={suggestions} onClose={onClose} />
            ) : (
              !isLoadingSuggestions && (
                <EmptySuggestions
                  topSearchedProducts={topSearchedProducts}
                  topSearchedProductCategories={topSearchedProductCategories}
                />
              )
            )
          ) : (
            <SearchRecommendations
              topSearchedProducts={topSearchedProducts}
              topSearchedProductCategories={topSearchedProductCategories}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchSearchSuggestionsData: (term, outputChannel) =>
      dispatch(fetchSearchSuggestionsData(term, outputChannel)),
    fetchTopSearchedProducts: (outputChannel) => dispatch(fetchTopSearchedProducts(outputChannel)),
    fetchTopSearchedProductCategories: () => dispatch(fetchTopSearchedProductCategories()),
  };
};

const mapStateToProps = (state: BaseStore) => {
  return { ...state.search };
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchDrawer);
